import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import "./Login.css";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw]   = useState(false);

  useEffect(() => {
    const role = localStorage.getItem("ai2_role");
    if (!role) return;
    const routeMap: Record<string, string> = {
      admin: "/dashboard/manager", manager: "/dashboard/manager",
      frontdesk: "/dashboard/manager", reception: "/dashboard/manager",
      spa: "/dashboard/spa", spa_manager: "/dashboard/spa",
      room_service: "/dashboard/roomservice",
      room_service_manager: "/dashboard/roomservice/manager",
      housekeeper: "/dashboard/housekeeping",
      housekeeper_manager: "/dashboard/housekeeping/manager",
      livechat: "/dashboard/manager/guests",
    };
    if (routeMap[role]) navigate(routeMap[role], { replace: true });
    else localStorage.clear(); // unknown/removed role — clear session
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_URL ?? "http://localhost:5050"}/api/auth/login`, { email, password });
      const { token, role, name } = res.data;
      localStorage.setItem("ai2_token", token);
      localStorage.setItem("ai2_role", role);
      if (name) localStorage.setItem("ai2_name", name);
      const routeMap: Record<string, string> = {
        admin: "/dashboard/manager", manager: "/dashboard/manager",
        frontdesk: "/dashboard/manager", reception: "/dashboard/manager",
        spa: "/dashboard/spa", spa_manager: "/dashboard/spa",
        room_service: "/dashboard/roomservice",
        room_service_manager: "/dashboard/roomservice/manager",
        housekeeper: "/dashboard/housekeeping",
        housekeeper_manager: "/dashboard/housekeeping/manager",
        livechat: "/dashboard/manager/guests",
      };
      if (routeMap[role]) window.location.href = routeMap[role];
      else setError("Unknown role: " + role);
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Login failed. Check the browser console for details.";
      setError(msg);
      console.error("Login error:", err.response?.status, err.response?.data, err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ln-root">
      <div className="ln-card">
        <div className="ln-logo">
          <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="48" height="48">
            <circle cx="40" cy="9" r="3" fill="#C9A84C"/>
            <line x1="40" y1="11" x2="8" y2="79" stroke="#C9A84C" strokeWidth="2.2" strokeLinecap="square"/>
            <line x1="40" y1="11" x2="72" y2="79" stroke="#C9A84C" strokeWidth="2.2" strokeLinecap="square"/>
            <path d="M26 57 A14 14 0 0 0 54 57" stroke="#C9A84C" strokeWidth="1.8" fill="none"/>
            <line x1="22" y1="57" x2="29" y2="57" stroke="#C9A84C" strokeWidth="1.8"/>
            <line x1="51" y1="57" x2="58" y2="57" stroke="#C9A84C" strokeWidth="1.8"/>
            <line x1="1" y1="79" x2="19" y2="79" stroke="#C9A84C" strokeWidth="2.8"/>
            <line x1="61" y1="79" x2="79" y2="79" stroke="#C9A84C" strokeWidth="2.8"/>
          </svg>
        </div>
        <h1 className="ln-title">Ahlaan.ai</h1>
        <p className="ln-sub">Sign in to your dashboard</p>

        <form onSubmit={handleLogin} className="ln-form">
          {error && <div className="ln-error">{error}</div>}

          <div className="ln-field">
            <label className="ln-label">Email</label>
            <input
              className="ln-input"
              type="email"
              placeholder="you@hotel.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="ln-field">
            <label className="ln-label">Password</label>
            <div className="ln-pw-wrap">
              <input
                className="ln-input"
                type={showPw ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button type="button" className="ln-pw-toggle" onClick={() => setShowPw(p => !p)}>
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <button type="submit" className="ln-submit" disabled={loading}>
            {loading ? <span className="ln-spinner" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
